import { useEffect, useState } from 'react';

import { CheckCircle, ChevronRight, CreditCard, MapPin, Shield, Users } from 'lucide-react';

export default function ViewInscrire() {
  const [licenseState, setLicenseState] = useState<'idle' | 'typing' | 'loading' | 'valid'>('idle');

  useEffect(() => {
    const t1 = setTimeout(() => setLicenseState('typing'), 700);
    const t2 = setTimeout(() => setLicenseState('loading'), 1800);
    const t3 = setTimeout(() => setLicenseState('valid'), 3100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const isValid = licenseState === 'valid';

  return (
    <div className='grid md:grid-cols-2 gap-8 items-start demo-view-enter'>
      {/* Left — form */}
      <div className='space-y-5'>
        {/* Step progress */}
        <div className='flex items-center gap-2'>
          {(['Équipe', 'Récap', 'Paiement'] as const).map((s, i) => (
            <div key={s} className='flex items-center gap-2'>
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                  i === 0
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 dark:shadow-blue-900/40'
                    : 'bg-slate-100 border border-slate-200 text-slate-400 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500'
                }`}
              >
                {i === 0 ? '1' : <span className='text-[10px]'>{i + 1}</span>}
              </div>
              <span
                className={`text-xs font-medium hidden sm:block ${i === 0 ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-600'}`}
              >
                {s}
              </span>
              {i < 2 && <ChevronRight className='w-3 h-3 text-slate-300 dark:text-slate-700' />}
            </div>
          ))}
        </div>

        <h3 className='text-xl font-black text-slate-900 dark:text-white'>Composition de l'équipe</h3>

        {/* Captain */}
        <div className='bg-emerald-50 border border-emerald-200/60 dark:bg-slate-800/70 dark:border-emerald-500/30 rounded-2xl p-4'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-black text-white text-sm shadow-lg shadow-blue-600/30 dark:shadow-blue-900/40 shrink-0'>
              PD
            </div>
            <div className='flex-1 min-w-0'>
              <p className='font-bold text-slate-900 dark:text-white text-sm'>
                Pierre Dubois{' '}
                <span className='text-slate-500 dark:text-slate-400 font-normal text-xs'>(vous · capitaine)</span>
              </p>
              <p className='text-xs text-slate-500 dark:text-slate-400 mt-0.5'>Licence FFPJP #06900123</p>
            </div>
            <CheckCircle className='w-5 h-5 text-emerald-500 dark:text-emerald-400 shrink-0' />
          </div>
        </div>

        {/* Partner field — animated */}
        <div
          className={`border rounded-2xl p-4 transition-all duration-500 ${
            isValid
              ? 'bg-emerald-50 border-emerald-200/60 dark:bg-slate-800/70 dark:border-emerald-500/30'
              : 'bg-slate-50 border-slate-200 dark:bg-slate-800/40 dark:border-slate-700'
          }`}
        >
          <p className='text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3'>Partenaire</p>
          <div className='flex items-center gap-3'>
            {isValid ? (
              <>
                <div className='w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center font-black text-white text-sm shrink-0'>
                  MB
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='font-bold text-slate-900 dark:text-white text-sm'>Marc Bernard</p>
                  <p className='text-xs text-emerald-600 dark:text-emerald-400 mt-0.5'>
                    Licence #06901456 · Auto-déclaration enregistrée
                  </p>
                </div>
                <CheckCircle className='w-5 h-5 text-emerald-500 dark:text-emerald-400 shrink-0' />
              </>
            ) : (
              <>
                <div className='w-10 h-10 bg-slate-200 border border-slate-300 dark:bg-slate-700 dark:border-slate-600 rounded-full flex items-center justify-center shrink-0'>
                  <Users className='w-4 h-4 text-slate-400 dark:text-slate-500' />
                </div>
                <div className='flex-1'>
                  <div className='bg-slate-100 border border-slate-200 dark:bg-slate-700/60 dark:border-slate-600 rounded-xl px-3 py-2 text-sm text-slate-600 dark:text-slate-300 font-mono'>
                    {licenseState === 'idle' ? (
                      <span className='text-slate-400 dark:text-slate-500'>Numéro de licence…</span>
                    ) : licenseState === 'typing' ? (
                      '069014…'
                    ) : (
                      '06901456'
                    )}
                  </div>
                  {licenseState === 'loading' && (
                    <p className='text-xs text-blue-500 dark:text-blue-400 mt-1.5 flex items-center gap-1.5'>
                      <span className='w-3 h-3 border-2 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full animate-spin inline-block' />
                      Saisie du numéro de licence…
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className='flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 dark:bg-slate-900/50 dark:border-slate-800 rounded-xl px-4 py-3'>
          <Shield className='w-3.5 h-3.5 shrink-0 mt-0.5' />
          <span>
            Le numéro de licence FFPJP est renseigné par le joueur lors de l'inscription et utilisé pour l'organisation
            et les exports.
          </span>
        </div>
      </div>

      {/* Right — recap + CTA */}
      <div className='space-y-4'>
        {/* Competition recap */}
        <div className='bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:border-slate-800 rounded-2xl p-5'>
          <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4'>Récapitulatif</p>
          <p className='font-bold text-slate-900 dark:text-white mb-1'>Grand Prix Pétanque d'Été — Gerland</p>
          <div className='space-y-2 text-sm text-slate-500 dark:text-slate-400 mb-5'>
            <div className='flex items-center gap-2'>
              <MapPin className='w-3.5 h-3.5 shrink-0' />
              Lyon, 69 · 13 Juin 2026
            </div>
            <div className='flex items-center gap-2'>
              <Users className='w-3.5 h-3.5 shrink-0' />
              Doublette · {isValid ? 'Dubois / Bernard' : 'Dubois / …'}
            </div>
          </div>
          <div className='border-t border-slate-200 dark:border-slate-800 pt-4 flex items-baseline justify-between'>
            <span className='text-slate-500 dark:text-slate-400 text-sm'>Montant</span>
            <span className='text-2xl font-black text-slate-900 dark:text-white'>30 €</span>
          </div>
        </div>

        {/* Pay CTA */}
        <button
          className={`w-full py-4 rounded-2xl font-black text-base flex items-center justify-center gap-3 transition-all duration-500 ${
            isValid
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-xl hover:shadow-blue-600/30 dark:hover:shadow-blue-900/40 hover:-translate-y-0.5'
              : 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-600'
          }`}
        >
          <CreditCard className='w-5 h-5' />
          {isValid ? 'Confirmer et payer 15 €' : 'En attente du partenaire…'}
        </button>

        <div className='flex items-center justify-center gap-5 text-xs text-slate-500 dark:text-slate-600'>
          <span className='flex items-center gap-1.5'>
            <Shield className='w-3 h-3' />
            Stripe sécurisé
          </span>
          <span className='flex items-center gap-1.5'>
            <CheckCircle className='w-3 h-3' />
            Reçu instantané
          </span>
        </div>

        <p className='text-[10px] text-slate-500 dark:text-slate-700 text-center leading-relaxed'>
          BoulesConnect est une plateforme logicielle. Les informations du concours sont déclarées par l'organisateur.
        </p>
      </div>
    </div>
  );
}
